import React from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { ChartPoint } from '@/domain/entities';

interface LineChartProps {
  data: ChartPoint[];
  title: string;
  type: 'count' | 'total';
  color?: string;
}

export const LineChart: React.FC<LineChartProps> = ({ 
  data, 
  title, 
  type,
  color = '#4F46E5',
}) => {
    if (!data || data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
        <div className="flex items-center justify-center h-40 text-gray-500 dark:text-gray-400 text-sm">
          Нет данных для отображения
        </div>
      </div>
    );
  }

  // Преобразуем данные для Recharts
  const chartData = data.map((point) => ({
    date: point.date,
    value: type === 'count' ? point.count : point.total,
    formattedDate: new Date(point.date).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
    }),
  }));

  const formatValue = (value: number) => {
    if (type === 'total') {
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    }
    return value.toString();
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
            {payload[0].payload.formattedDate}
          </p>
          <p className="text-sm" style={{ color }}>
            {type === 'count' ? 'Количество: ' : 'Сумма: '}
            <span className="font-semibold">{formatValue(payload[0].value)}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
      <div className="w-full" style={{ height: '200px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 5, right: 20, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id={`gradient-${type}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" className="dark:stroke-gray-700" />
            <XAxis
              dataKey="formattedDate"
              stroke="#6B7280"
              className="dark:stroke-gray-400"
              fontSize={11}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              stroke="#6B7280"
              className="dark:stroke-gray-400"
              fontSize={11}
              tickLine={false}
              axisLine={false}
              width={50}
              tickFormatter={(value) => {
                if (type === 'total') {
                  // Для больших сумм показываем сокращенно
                  if (value >= 1000000) {
                    return `${(value / 1000000).toFixed(1)}М`;
                  }
                  if (value >= 1000) {
                    return `${(value / 1000).toFixed(0)}К`;
                  }
                }
                return value.toString();
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="value"
            stroke={color}
              strokeWidth={2}
              fill={`url(#gradient-${type})`}
              dot={{ fill: color, r: 3 }}
              activeDot={{ r: 5, fill: color }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
